<template>
    <div
        v-if="!loading"
        class="module"
    >
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('admin-relays')"
            >
                <span class="icon-chevron-left" />
            </a>

            <h1 class="has-back-btn">Relay Details</h1>
            <span class="module__header--tag">BETA</span>
        </header>

        <section class="module__body">
            <div class="relay-info-header">
                <relay-images-gallery
                    :images="images"
                    :relayId="relayId"
                    :relayType="type"
                />
                <div class="relay-info-block">
                    <div class="header">
                        <h1 data-test-id="header-site-name">{{ siteName }}</h1>
                        <div class="actions">
                            <router-link
                                v-if="showEdit"
                                tag="a"
                                :to="`/${organizationSlug}/relays/${relayId}/edit`"
                                class="edit icon-edit"
                                title="Edit"
                                data-test-id="edit-icon"
                            />
                            <a
                                v-if="showDelete"
                                class="delete icon-delete"
                                href="#"
                                title="Delete"
                                data-test-id="delete-icon"
                                @click.prevent="confirmDelete"
                            />
                            <a
                                v-if="canDownloadIdentity"
                                class="icon-key"
                                href="#"
                                title="Download credentials"
                                data-test-id="delete-identity-icon"
                                @click.prevent="downloadIdentity"
                            />
                        </div>
                    </div>
                    <p
                        data-test-id="header-site-description"
                        class="relay-info-block__description"
                    >
                        {{ description }}
                    </p>
                </div>
                <div class="relay-map">
                    <div>
                        <router-link
                            v-if="hasValidLocation"
                            tag="div"
                            class="relay-map-hitbox"
                            data-test-id="relay-map-hitbox"
                            :to="`/${organizationSlug}/relays/map?relayId=${relayId}`"
                        />
                        <h2>Location</h2>
                        <p
                            v-if="hasValidLocation"
                            data-test-id="display-location-coords"
                        >
                            {{ latitude }}, {{ longitude }}
                        </p>
                        <p
                            v-else
                            data-test-id="display-location-coords-na"
                        >
                            N/A
                        </p>
                        <router-link
                            v-if="hasValidLocation"
                            tag="a"
                            class="location icon-location-white"
                            :to="`/${organizationSlug}/relays/map?relayId=${relayId}`"
                            title="Location"
                            data-test-id="location-map-icon"
                        />
                        <a
                            v-else
                            class="location icon-no-location"
                            title="Location Unavailable"
                            data-test-id="no-location-map-icon"
                            @click.prevent=""
                        />
                    </div>
                </div>
            </div>
            <ul class="relay-info-details">
                <li>
                    <h2>Name</h2>
                    <span data-test-id="site-name">{{ siteName }}</span>
                </li>
                <li>
                    <h2>Tower Mounted?</h2>
                    <span data-test-id="is-tower-mounted">{{ $filters.convertBoolean(isTowerMounted) }}</span>
                </li>
                <li>
                    <h2>Elevation (m)</h2>
                    <span data-test-id="altitude">{{ displayAltitude }}</span>
                </li>
                <li>
                    <h2>{{ relayType.label }} SN #</h2>
                    <span data-test-id="serial-number">{{ serialNumber }}</span>
                </li>
                <li>
                    <h2>Customer Asset #</h2>
                    <span data-test-id="customer-asset-number">{{ assetNumber }}</span>
                </li>
                <li>
                    <h2>Power Type</h2>
                    <span data-test-id="power-type">{{ powerTypeObject?.label }}</span>
                </li>

                <template v-if="powerTypeObject?.type === relayPowerTypes.AC.type">
                    <li>
                        <h2>Source Type</h2>
                        <span data-test-id="ac-power-source-type">{{ acPowerSourceType }}</span>
                    </li>
                    <li>
                        <h2>Voltage</h2>
                        <span data-test-id="ac-power-voltage">{{ acPowerVoltage }}</span>
                    </li>
                    <li>
                        <h2>Amps</h2>
                        <span data-test-id="ac-power-circuit-amps">{{
                            acPowerCircuitAmps ? parseFloat(acPowerCircuitAmps) : ''
                        }}</span>
                    </li>
                </template>

                <template v-if="powerTypeObject?.type === relayPowerTypes.BATTERY.type">
                    <li>
                        <h2>External Battery?</h2>
                        <span data-test-id="is-battery-external">{{ $filters.convertBoolean(isBatteryExternal) }}</span>
                    </li>
                    <li v-if="isBatteryExternal">
                        <h2>Battery Type</h2>
                        <span data-test-id="battery-type">{{ batteryType }}</span>
                    </li>
                </template>

                <template v-if="powerTypeObject?.type === relayPowerTypes.SOLAR.type">
                    <li>
                        <h2>Panel Type</h2>
                        <span data-test-id="solar-power-panel-type">{{ solarPowerPanelType }}</span>
                    </li>
                    <li>
                        <h2>Wattage</h2>
                        <span data-test-id="solar-power-wattage">{{
                            solarPowerWattage ? parseFloat(solarPowerWattage) : ''
                        }}</span>
                    </li>
                </template>
                <li>
                    <h2>Install Date (UTC)</h2>
                    <span data-test-id="install-date">{{ installedAt }}</span>
                </li>
                <li>
                    <h2>Installer</h2>
                    <span data-test-id="installer">{{ installedBy }}</span>
                </li>
            </ul>
            <div class="relay-radios">
                <tab-view
                    :activeTab="activeRadioIndex"
                    :tabs="radioTabs"
                    @tab-clicked="tabClicked"
                ></tab-view>
                <health-checks :radio="activeRadio"></health-checks>
            </div>
        </section>

        <ConfirmDialog
            group="headless"
            :pt="confirmDialogPT"
        >
            <template #container="{ message, acceptCallback, rejectCallback }">
                <div
                    class="confirm-container"
                    data-test-id="relay-confirm-delete-container"
                >
                    <div class="confirm-content">
                        <p
                            class="header"
                            data-test-id="relay-confirm-delete-header"
                        >
                            {{ message.header }}
                        </p>
                        <p
                            class="body"
                            data-test-id="relay-confirm-delete-message"
                        >
                            {{ message.message }}
                        </p>
                    </div>
                    <div class="confirm-buttons">
                        <button
                            class="btn"
                            data-test-id="relay-confirm-delete-cancel"
                            :disabled="deleting"
                            @click="rejectCallback"
                        >
                            Cancel
                        </button>
                        <button
                            class="btn btn-primary warning"
                            :class="{ activeNoLabel: deleting }"
                            data-test-id="relay-confirm-delete-ok"
                            :disabled="deleting"
                            @click="doDelete(acceptCallback)"
                        >
                            <span>Yes, Delete</span>
                        </button>
                    </div>
                </div>
            </template>
        </ConfirmDialog>
    </div>
</template>

<script>
import ConfirmDialog from 'primevue/confirmdialog'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import hasBackButton from '@/mixins/hasBackButton'
import RelayImagesGallery from './RelayImagesGallery.vue'
import TabView from '@/components/TabView.vue'
import HealthChecks from './HealthChecks.vue'
import organizations from '../organizations/index.vue'
import relayTypes from '@/constants/relay-types'
import canToast from '@/mixins/canToast'
import relayPowerTypes, { byValue as getRelayPowerType } from '@/constants/relay-power-types'
import { byValue as getAcSourceType } from '@/constants/relay-ac-power-sources'
import { byValue as getAcVoltage } from '@/constants/relay-ac-power-voltages'
import featureFlags from '../../../constants/feature-flags.js'

export default {
    components: { ConfirmDialog, RelayImagesGallery, TabView, HealthChecks },
    mixins: [canToast, needsOrganizationSlug, hasBackButton],

    data() {
        const { hasPermission } = this.$store.getters
        const showEdit = hasPermission('update assets')
        const showDelete = hasPermission('delete assets')

        return {
            relayId: this.$route.params.id,
            radioSerialNumber: this.$route.params.radioSerialNumber,
            siteName: '',
            description: '',
            images: [],
            latitude: 0.0,
            longitude: 0.0,
            altitude: 0.0,
            serialNumber: '',
            stockNumber: null,
            assetNumber: '',
            installedAt: '',
            installedBy: '',
            type: null,
            isTowerMounted: null,
            powerTypeObject: null,
            solarPowerPanelType: null,
            solarPowerWattage: null,
            acPowerCircuitAmps: null,
            acPowerSourceType: null,
            acPowerVoltage: null,
            isBatteryExternal: null,
            batteryType: null,
            radios: [],
            loading: true,
            showEdit,
            showDelete,
            deleting: false,
            relayPowerTypes,
            canDownloadIdentity: false,
            urn: null,
            confirmDialogPT: {
                root: {
                    style: { border: 'none' }, // Remove border from the main dialog container
                },
                header: {
                    style: { border: 'none' }, // Remove border from the header
                },
                content: {
                    style: { border: 'none' }, // Remove border from the content area
                },
                footer: {
                    style: { border: 'none' }, // Remove border from the footer
                },
            },
        }
    },

    computed: {
        organizations() {
            return organizations
        },
        radioTabs() {
            const tabs = this.radios.map((radio) => {
                return {
                    label: radio.label,
                    serial_number: radio.serial_number,
                }
            })
            if (tabs.length > 0) {
                tabs.unshift({ label: 'View All' })
            }

            return tabs
        },
        activeRadio() {
            return this.radios[this.activeRadioIndex - 1] ?? null
        },
        activeRadioIndex() {
            if (this.radioSerialNumber) {
                return this.radioTabs.findIndex((tab) => tab.serial_number === this.radioSerialNumber)
            }

            return 0
        },
        displayAltitude() {
            const { altitude } = this.$data
            if (!Number.isNaN(parseInt(altitude))) {
                return altitude
            }
            return 'N/A'
        },
        hasValidLocation() {
            return this.latitude !== null && this.longitude !== null
        },
        relayType() {
            return relayTypes[this.type]
        },
    },

    watch: {
        '$route.params.radioSerialNumber': {
            handler(newValue) {
                this.radioSerialNumber = newValue
            },
        },
    },

    mounted() {
        this.$http.get(`/${this.organizationSlug}/relays/${this.relayId}`).then(({ data }) => {
            this.siteName = data.site_name
            this.images = data.images
            this.description = data.description
            this.latitude = data.latitude
            this.longitude = data.longitude
            this.altitude = data.altitude
            this.serialNumber = data.serial_number
            this.stockNumber = data.stock_number
            this.assetNumber = data.asset_number
            this.installedAt = data.installed_at
            this.installedBy = data.installed_by
            this.isTowerMounted = data.is_tower_mounted
            this.powerTypeObject = getRelayPowerType(data.power_type)
            this.solarPowerPanelType = data.solar_power_panel_type
            this.solarPowerWattage = data.solar_power_wattage
            this.acPowerCircuitAmps = data.ac_power_circuit_amps
            this.acPowerSourceType = getAcSourceType(data.ac_power_source_type)?.label
            this.acPowerVoltage = getAcVoltage(data.ac_power_voltage)?.label
            this.isBatteryExternal = data.is_battery_external
            this.batteryType = data.battery_type
            this.type = data.type
            this.urn = data.urn
            this.canDownloadIdentity =
                this.type === relayTypes.ADVANCED.type &&
                this.$store.getters.hasPermission('create temporary asset identity') &&
                this.$store.getters.hasFeatureFlag(featureFlags.ADVANCED_RELAYS_ENABLED) &&
                data.assembly?.members_with_asset.some((asset) => asset.asset_type === 'asset_smart_device')

            this.radios =
                data.assembly?.members_with_asset
                    .filter((member) => member.asset !== null && member.asset_type === 'asset_radio')
                    .map((member) => ({
                        label: member.label,
                        serial_number: member?.asset?.serial_number,
                        id: member.asset_id,
                    })) ?? []

            this.loading = false
        })
    },

    methods: {
        downloadIdentity() {
            const password = window.prompt('Provide a password')
            const url = `${this.organizationSlug}/certificates/temporary/${encodeURIComponent(this.urn)}`
            this.$http
                .post(
                    url,
                    {
                        password,
                        password_confirmation: password,
                    },
                    {
                        responseType: 'blob',
                    }
                )
                .catch(async (error) => {
                    this.toast(await error.response.data.text(), 'error', 6000)
                })
                .then((response) => {
                    const contentDisposition = response.headers['content-disposition']
                    let filename = 'download.json'

                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="?([^"]+)"?/)
                        const [, extractedFilename] = match

                        if (extractedFilename) {
                            filename = extractedFilename
                        }
                    }

                    // Create a URL for the blob data
                    const href = window.URL.createObjectURL(response.data)

                    // Create a temporary anchor element
                    const link = document.createElement('a')
                    link.href = href
                    link.setAttribute('download', filename) // Set the file name
                    document.body.appendChild(link) // Append to body to make it clickable in all browsers

                    // Trigger the download
                    link.click()

                    // Clean up and remove the link and revoke the object URL
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(href)
                })
        },
        tabClicked(tabIndex) {
            if (tabIndex === 0) {
                this.$router.push(`/${this.organizationSlug}/relays/${this.relayId}/details`)
            } else {
                const radioSerialNumber = this.radioTabs[tabIndex].serial_number
                this.$router.push(`/${this.organizationSlug}/relays/${this.relayId}/details/${radioSerialNumber}`)
            }
        },

        confirmDelete() {
            this.$confirm.require({
                group: 'headless',
                message:
                    'Are you sure you want to delete this relay? You will also lose all heath check data. This action cannot be undone.',
                header: `Delete ${this.siteName ? this.siteName : 'Relay'}?`,
                accept: () => {},
                reject: () => {},
            })
        },

        doDelete(closeDialog) {
            this.deleting = true
            this.$http
                .delete(`${this.organizationSlug}/relays/${this.relayId}`)
                .then(() => {
                    this.deleting = false
                    closeDialog()
                    this.toast(`Relay successfully deleted!`)
                    this.$router.push({ name: 'admin-relays' })
                })
                .catch(() => {
                    this.deleting = false
                    closeDialog()
                    this.toast('There was a problem deleting the relay', 'error')
                })
        },
    },
}
</script>

<style lang="scss" scoped>
@use 'sass:color';

.relay-info-header {
    display: flex;
    flex-direction: row;
    justify-items: flex-start;
    flex-wrap: wrap;
    gap: 2em;

    > div {
        flex: 0 0 auto;
        width: 15%;
    }

    .relay-info-block {
        flex-grow: 2;
        h1 {
            font-weight: 800;
            font-size: 16px;
            margin: 16px 0;
        }

        &__description {
            font-size: 12px;
        }

        .header {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            justify-items: center;
            gap: 0.5rem;

            .actions {
                display: flex;
                flex-direction: row;
                align-self: center;
                font-size: 1.5rem;
                gap: 0.5rem;
                height: 26px;

                a {
                    width: 26px;
                    height: 26px;
                    display: block;

                    &:before {
                        content: '';
                        background-size: 26px 26px;
                        background-repeat: no-repeat;
                        background-position: top left;
                        width: 26px;
                        height: 26px;
                        display: block;
                        flex-shrink: 0;
                    }
                }

                a.icon-location:before {
                    background-image: url(@images/relays/icon-map-location.svg);
                }

                a.icon-location-white:before {
                    background-image: url(@images/relays/icon-map-location-white.svg);
                }

                a.icon-no-location:before {
                    background-image: url(@images/relays/icon-map-location-missing.svg);
                }

                a.icon-edit:before {
                    background-image: url(@images/relays/icon-edit.svg);
                }

                a.icon-delete:before {
                    background-image: url(@images/relays/icon-delete.svg);
                }

                a.icon-key:before {
                    background-image: url(@images/key-white.svg);
                }
            }
        }
    }

    .relay-map {
        padding: 0 1em;
        background: url(@images/relays/location-map.jpg);
        background-size: cover;
        background-position: center center;
        height: 142px;
        width: 250px;
        max-width: 250px;
        border-radius: 8px;
        display: flex;
        align-items: flex-end;
        font-size: 11px;
        position: relative;

        div {
            display: flex;
            margin-bottom: 10px;
            flex-wrap: wrap;
            flex: 1 0 auto;
            justify-content: space-between;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            h2 {
                font-size: 13px;
                font-family: 'Avenir Heavy', serif;
                font-weight: bold;
                flex-basis: 100%;
            }

            .relay-map-hitbox {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 90%;
                z-index: 1;
            }
        }
        a,
        p,
        h2 {
            z-index: 2;
        }
        a {
            width: 26px;
            height: 26px;
            display: block;
            &:before {
                content: '';
                background-size: 26px 26px;
                background-repeat: no-repeat;
                background-position: top left;
                width: 26px;
                height: 26px;
                display: block;
                flex-shrink: 0;
            }
        }

        a.icon-location:before {
            background-image: url(@images/relays/icon-map-location.svg);
        }

        a.icon-location-white:before {
            background-image: url(@images/relays/icon-map-location-white.svg);
        }

        a.icon-location-white:hover:before {
            background-image: url(@images/relays/icon-map-location-white.svg);
            transform-origin: bottom right;
            opacity: 0.7;
        }

        a.icon-no-location:before {
            background-image: url(@images/relays/icon-map-location-missing.svg);
        }

        a.icon-edit:before {
            background-image: url(@images/relays/icon-edit.svg);
        }

        a.icon-delete:before {
            background-image: url(@images/relays/icon-delete.svg);
        }
    }
}

ul.relay-info-details {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    margin-top: 17px;
    font-size: 14px;
    gap: 1em;

    li {
        margin-bottom: 1em;
        flex: 1 1 auto;

        h2 {
            font-size: 14px;
            font-weight: 800;
            line-height: 20px;
            margin-bottom: 5px;
            font-family: 'Avenir Heavy';
        }

        span {
            font-size: 14px;
            font-weight: 500;
        }
    }
}

.relay-radios {
    margin-top: 30px;
}

.confirm-container {
    width: 484px;
    //height: 212px;
    background-color: #2b2930;
    color: #f1f1f1;
    padding: 24px;
    border-radius: 8px;
    border-width: 0px;

    .confirm-content {
        .header {
            font-size: 24px;
            font-family: 'Avenir', sans-serif;
            margin-bottom: 16px;
            text-overflow: ellipsis;
            overflow: hidden;
            text-wrap: nowrap;
        }

        .body {
            margin-bottom: 24px;
            font-size: 16px;
            font-family: 'Avenir', sans-serif;
        }
    }

    .confirm-buttons {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        padding-top: 12px;
        padding-bottom: 16px;
        width: 100%;
        gap: 12px;

        .warning {
            background-color: #ffb3af;
            border-color: #ffb3af;
            color: #601410;

            &:not([disabled='disabled']):hover,
            &:not([disabled='disabled']):focus {
                background-color: color.adjust(#ffb3af, $lightness: 10%);
                border-color: color.adjust(#ffb3af, $lightness: 10%);
            }
        }

        button.activeNoLabel span {
            visibility: hidden;
        }
    }
}
</style>
